/*
 * Copyright (c) Nexign, JSC, 2020-2021.
 */
/**
 * Saga for SettingsPositions component
 *
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @version 1.14.1
 * @since 1.3.0
 */

import { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';

import { SETTINGS_POSITIONS } from '../../../../modules/app/Api';
import { EHttpMethod } from '../../../../modules/app/appConstants';
import { checkResponseErrorWithModal, requestSaga } from '../../../../sagas/requestSaga';
import { formQueryParamsString } from '../../../../sagas/utils/formQueryParamsString';
import { settingsLoaderStartAction, settingsLoaderStopAction } from '../../settingsActions';
import {
    addSettingsPositionFailure,
    addSettingsPositionSuccess,
    getSettingsPositionsListAction,
    getSettingsPositionsListFailure,
    getSettingsPositionsListSuccess,
    removeSettingsPositionFailure,
    removeSettingsPositionSuccess,
} from './settingsPositionsActions';
import {
    ADD_SETTINGS_POSITION_ACTION,
    GET_SETTINGS_POSITIONS_LIST_ACTION,
    REMOVE_SETTINGS_POSITION_ACTION,
} from './settingsPositionsConstants';
import { makeSelectSettingsPositionsList, makeSelectSettingsPositionsListSearch } from './settingsPositionsSelectors';
import {
    ISettingsPositionsItem,
    ISettingsPositionsItemResponse,
    TAddSettingsPositionAction,
    TGetSettingsPositionsListAction,
    TRemoveSettingsPositionAction,
} from './settingsPositionsTypes';

export function* addSettingsPositionSaga(action: TAddSettingsPositionAction): SagaIterator {
    try {
        yield put(settingsLoaderStartAction());
        const url = `${SETTINGS_POSITIONS}?name=${action.payload}`;
        yield call(requestSaga, url, 'POST');
        yield put(addSettingsPositionSuccess());
        yield put(getSettingsPositionsListAction({ start: true }));
    } catch (e: any) {
        yield put(addSettingsPositionFailure(e));

        yield call(checkResponseErrorWithModal, e);
    } finally {
        yield put(settingsLoaderStopAction());
    }
}

export function* removeSettingsPositionSaga(action: TRemoveSettingsPositionAction): SagaIterator {
    try {
        yield put(settingsLoaderStartAction());
        const url = `${SETTINGS_POSITIONS}/${action.payload}`;
        yield call(requestSaga, url, EHttpMethod.DELETE);
        yield put(removeSettingsPositionSuccess({ positionId: action.payload }));
    } catch (e: any) {
        yield put(removeSettingsPositionFailure(e));

        yield call(checkResponseErrorWithModal, e);
    } finally {
        yield put(settingsLoaderStopAction());
    }
}

export function* getSettingsPositionsListSaga(action: TGetSettingsPositionsListAction): SagaIterator {
    try {
        const prevSearch = yield select(makeSelectSettingsPositionsListSearch());
        const search = action.payload.search !== undefined ? action.payload.search : prevSearch;
        const positionsList: ISettingsPositionsItem[] = yield select(makeSelectSettingsPositionsList());
        const url = SETTINGS_POSITIONS;
        const queryString = formQueryParamsString({
            from: action.payload.start ? 0 : positionsList.length,
            term: search,
            size: 24,
        });
        const list: ISettingsPositionsItemResponse[] =
            yield call(requestSaga, url + queryString, EHttpMethod.GET, null, { abortControllerId: url });
        yield put(getSettingsPositionsListSuccess({
            positionsList: [...positionsList, ...list.map(item => ({ id: item.id, name: item.def }))],
            hasMore: list.length === 24,
        }));
    } catch (e: any) {
        yield put(getSettingsPositionsListFailure(e));
    }
}

export function* settingsPositionsSagas(): SagaIterator {
    yield all([
        takeEvery(ADD_SETTINGS_POSITION_ACTION, addSettingsPositionSaga),
        takeEvery(GET_SETTINGS_POSITIONS_LIST_ACTION, getSettingsPositionsListSaga),
        takeEvery(REMOVE_SETTINGS_POSITION_ACTION, removeSettingsPositionSaga),
    ]);
}
